import * as L from 'leaflet';
import 'leaflet.markercluster';
import { Component, ElementRef, EventEmitter, Injector, OnDestroy, OnInit, Output } from '@angular/core';
import { DataSource } from '../../interfaces/data-source.interface';
import { DataSourceConfig } from '../../interfaces/data-source-config.interface';
import { FilterInput } from '../../interfaces/filter-input.interface';
import { MarkerClusterGroup } from 'leaflet.markercluster';
import { MatDialog } from '@angular/material';
import { NavigationAction } from '../table/interfaces/screen-config-action.interface';
import { SellionsData } from '../../interfaces/sellions-data.interface';
import { Utils } from '../../utils/utils.class';
import { ScreenActions } from '../../interfaces/screen-actions.interface';
import { ComponentWithNavigationAction, ComponentWithProcessAction } from '../../interfaces/component-actions.interface';
import { ProcessAction } from '../../interfaces/process-action.interface';

@Component({
  selector: 'sellions-layout-renderer-map',
  templateUrl: './map.component.html',
})
export class MapComponent implements OnInit, OnDestroy, ComponentWithNavigationAction, ComponentWithProcessAction {
  @Output() navigationAction: EventEmitter<NavigationAction> = new EventEmitter<NavigationAction>();
  @Output() screenAction: EventEmitter<ProcessAction> = new EventEmitter<ProcessAction>();

  public legendOpen = false;
  public map: L.Map;

  screenActions: ScreenActions;

  private currentPosition = {
    latitude: 52.2521016,
    longitude: 21.0380357,
  };
  private markerCluster: MarkerClusterGroup;
  private dataSourcesConfig: DataSourceConfig[];
  private dataSources: DataSource[] = [];
  private authToken: string;
  private iconSettings = {
    popupAnchor: [0, -9],
  };

  constructor(private dialog: MatDialog, private elementRef: ElementRef, private injector: Injector) {
    window.addEventListener('keyboardDidHide', () => {
      if (this.map) {
        this.map.invalidateSize();
      }
    });
  }

  public ngOnInit() {
    this.setDataSources(this.dataSourcesConfig);
    this.getData().then((data) => this.addMarkersToCluster(data));
    if (!this.map) this.createMap();
    this.setGeolocation();
  }

  private createMap() {
    this.map = L.map('map', { attributionControl: false }).setView([this.currentPosition.latitude, this.currentPosition.longitude], 9);
    L.tileLayer('https://maps.visuu.com/light_map/{z}/{x}/{y}.png', {
      maxZoom: 14,
      minZoom: 4,
    }).addTo(this.map);
  }

  private setGeolocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (response) => {
          this.currentPosition.latitude = response.coords.latitude;
          this.currentPosition.longitude = response.coords.longitude;
        },
        (error) => {
          console.warn("Couldn't establish current position. Using default of (52.1, 21.0)");
          this.currentPosition.latitude = 52.1;
          this.currentPosition.longitude = 21.0;
        },
        { timeout: 10000 },
      );
    }
  }

  public ngOnDestroy() {
    if (this.map) {
      this.map.off();
      this.map.remove();
    }
  }

  private setDataSources(config: DataSourceConfig[]) {
    config.forEach((dataSource: DataSourceConfig) => {
      const service = this.injector.get(dataSource.type);
      service.setConfig(dataSource);
      this.dataSources.push(service);
    });
  }

  private async getData(): Promise<SellionsData[]> {
    if (!this.dataSources) return Promise.resolve([]);
    const promises = this.dataSources.map((source) => source.getData(this.authToken));
    const locations = await Promise.all(promises);
    return this.flattenLocations(locations);
  }

  public search(query: string) {
    if (!this.dataSources || (query.length > 0 && query.length < 3)) return;

    const promises = this.dataSources.map((source) => source.search(query, this.authToken));
    Promise.all(promises).then((locations) => {
      this.addMarkersToCluster(this.flattenLocations(locations));
    });
  }

  private flattenLocations(locations: SellionsData[][]): SellionsData[] {
    return locations.reduce((accumulator, value) => accumulator.concat(value), []);
  }

  private addMarkersToCluster(locations: SellionsData[]) {
    if (this.markerCluster) this.map.removeLayer(this.markerCluster);

    this.markerCluster = (L as any).markerClusterGroup();
    this.map.removeLayer(this.markerCluster);

    locations.forEach((location) => {
      const marker = this.createMarker(location);
      if (marker) {
        this.markerCluster.addLayers(marker);
      }
    });

    if (this.markerCluster.getLayers().length > 0) {
      this.map.addLayer(this.markerCluster);
      this.map.fitBounds(this.markerCluster.getBounds(), { padding: [50, 50] });
    }
  }

  private createMarker(location: SellionsData): any {
    const config = location.dataSource.getConfig();

    const latitude = Utils.evaluateString(config.params.latitude, location);
    const longitude = Utils.evaluateString(config.params.longitude, location);

    if (longitude === '0' && latitude === '0') return;

    const marker = L.marker([latitude, longitude], {
      icon: L.divIcon({
        className: 'map-icon m' + location.id,
        ...this.iconSettings,
      }),
    })
      .bindPopup(
        this.getPopupHtml(location.id, Utils.evaluateString(config.params.title, location), Utils.evaluateString(config.params.content, location)),
      )
      .on('popupopen', this.activeMarkerAndAddDetailsListener(location, config.detailsOnClick.component, config.detailsOnClick.componentData.viewId))
      .on('popupclose', this.disableMarker(location.id));

    return marker;
  }

  private getPopupHtml(itemId: string | number, title: string, content: string): string {
    return (
      '<div id="mapMarker" class="map-popup">' +
      '<p class="map-popup-title">' +
      title +
      '</p>' +
      '<p class="map-popup-content">' +
      content +
      '</p>' +
      '<p class="map-popup-action"><a id="detailsLink' +
      itemId +
      '">' +
      'Zobacz więcej' +
      '</a></p></div>'
    );
  }

  private activeMarkerAndAddDetailsListener(location: SellionsData, component: string, viewId: string): Function {
    const emitOpenDetails = () =>
      this.navigationAction.emit({
        icon: '',
        label: '',
        component: component,
        componentData: { ...location, viewId: viewId },
      });

    return () => {
      const marker = this.elementRef.nativeElement.querySelector('.m' + location.id);
      if (marker) {
        marker.classList.add('active');
      } else {
        console.warn(`[MapComponent]: A marker for item with id: ${location.id} not found`);
      }

      const popUpDetailsLink = this.elementRef.nativeElement.querySelector('#detailsLink' + location.id);
      if (popUpDetailsLink) {
        popUpDetailsLink.addEventListener('touchstart', emitOpenDetails);
        popUpDetailsLink.addEventListener('click', emitOpenDetails);
      } else {
        console.warn(`[MapComponent]: A details link of popup for item with id: ${location.id} not found`);
      }
    };
  }

  private disableMarker(locationId: string): Function {
    return () => {
      const marker = this.elementRef.nativeElement.querySelector('.m' + locationId);
      if (marker) {
        marker.classList.remove('active');
      } else {
        console.warn(`[MapComponent]: A marker for item with id: ${locationId} not found`);
      }
    };
  }

  onButtonClick($event: ProcessAction): void {
    this.screenAction.emit($event);
  }
}



// WEBPACK FOOTER //
// ./src/submodules/sellions_layout_renderer/projects/sellions-layout-renderer-lib/src/lib/components/map/map.component.ts